// web-frontend/src/shared/services/carbonService.js
// BEXCO 기반 탄소 발자국 서비스 - Haversine 거리 계산 + API 연동 통합 버전
// 🔥 2단계 선택 UI 지원 (도/시 → 시/군/구) 추가
// 🔥 해외 좌표 기반 거리 계산 업그레이드
// 🌱 [2025-12-17 추가] syncCarbonDataFromLocal 메서드 - 로컬 설문 데이터 동기화
// 🔥 [2025-12-17 수정] trackCarbonActivity - 서버 응답값 기반 authStore 즉시 업데이트
// 🔥🔥🔥 [2025-12-18 수정] 게스트(device_id) 사용자 지원 추가
// 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정 (response.data → response)
// v4.9: apiClient 응답 구조 호환성 수정
import apiClient from './apiClient';
import { useAuthStore } from '../stores/authStore';
import logger from '../utils/logger';
import { getVenueCoords, getRegionDataset } from '../config/tenant';
import { HK_DISTRICT_COORDINATES } from '../utils/hkPlaces';

// =============================================================================
// 1. 상수 및 데이터 정의
// =============================================================================

// 📍 행사장 좌표 — 멀티테넌트로 활성 tenant 의 venue 사용
//    DSK: 부산 BEXCO 제1전시장 (35.1691, 129.1363)
//    AP : Hong Kong Convention and Exhibition Centre / HKCEC (22.2825, 114.1731)
// 'BEXCO_COORDS' 라는 이름은 호환을 위해 유지하되 실제 값은 venue 좌표.
const VENUE_COORDS = getVenueCoords();
const BEXCO_COORDS = { lat: VENUE_COORDS.lat, lon: VENUE_COORDS.lon };

// 🚗 교통수단별 CO2 배출량 (g/km/passenger) - 정밀 계산용
// HK 모드는 IPCC + 홍콩 환경보호서(EPD) 운수 부문 데이터를 참고했다.
// 항공은 단거리(domestic <1500km)/장거리(international ≥1500km) 분리.
const EMISSION_FACTORS_GRAMS = {
  // ---- 공통 (보행/자전거) ----
  walk_bike: 0,
  walk: 0,
  walking: 0,
  bicycle: 0,
  // ---- KR / 일반 도시 철도 ----
  subway: 30,
  train_normal: 40,
  train_ktx: 25,
  train: 25,
  // ---- 버스/택시 ----
  bus_eco: 20,
  bus_normal: 70,
  bus: 45,
  public_bus: 89,
  taxi: 192,
  // ---- 자가용 ----
  car_eco: 15,
  car_hybrid: 100,
  car_normal: 150,
  car: 150,
  car_gasoline: 192,
  car_diesel: 171,
  car_electric: 47,
  motorcycle: 103,
  // ---- 항공 (legacy 통합) ----
  airplane: 250,
  plane: 250,
  // ---- 항공 (도메스틱 ↔ 인터내셔널 분리) ----
  // domestic: 단거리(<1500km) 비행은 이착륙 비중이 높아 km 당 배출량이 더 큼
  airplane_domestic: 255,
  // international: 장거리는 순항 효율이 좋아 km 당 배출이 낮음
  airplane_international: 195,
  // ---- 🇭🇰 Hong Kong 전용 교통수단 ----
  // MTR (Mass Transit Railway, 지하철) — 중국 평균 대비 효율 좋은 전동차
  mtr: 28,
  // Light Rail (NT 경전철)
  light_rail: 30,
  // Hong Kong Tram (Ding Ding) — 100% 전기 운영, HK Island 만 운행
  hk_tram: 22,
  // Peak Tram (산악 푸니쿨라)
  peak_tram: 30,
  // KMB / Citybus / NWFB 등 더블데커 디젤 버스
  hk_bus: 80,
  // 미니버스 (Public Light Bus, Red/Green minibus)
  minibus: 89,
  // Star Ferry / NWFF — 디젤 페리 (per pax)
  ferry: 25,
  // Airport Express (HK 공항 고속철)
  airport_express: 25,
  other: 80,
};

// 기준 배출량 (자가용 가솔린/디젤) g/km
const BASELINE_EMISSION_GRAMS = 192;

// 🚗 교통수단별 CO2 배출량 (kg/km) - 기존 API 호환용
const EMISSION_FACTORS = {
  car: 0.2,
  bus: 0.05,
  train: 0.03,
  plane: 0.25,
  bicycle: 0,
  walk: 0,
  walking: 0,
  public_transit: 0.04,
  electric_car: 0.05,
  taxi: 0.2,
  airplane: 0.25,
};

// 도시별 BEXCO까지 거리 (km) - 오프라인 계산용 (기존 호환)
const DISTANCE_TO_BEXCO = {
  서울: 325,
  인천: 338,
  대전: 208,
  대구: 114,
  광주: 186,
  울산: 58,
  세종: 240,
  수원: 305,
  청주: 230,
  전주: 160,
  창원: 35,
  부산: 10,
  경기: 320,
  강원: 280,
  충북: 230,
  충남: 220,
  전북: 175,
  전남: 190,
  경북: 130,
  경남: 80,
  제주: 450,
  기타: 200,
};

// =============================================================================
// 🌏 [업그레이드] 해외 국가별 좌표 데이터 (설문 리스트 100% 매핑)
// =============================================================================

// 설문의 COUNTRIES_LIST에 있는 모든 국가의 수도/중심 좌표
const OVERSEAS_COORDINATES = {
  // 아시아
  KR: {
    lat: 37.5665,
    lon: 126.978,
    names: ['대한민국', 'south korea', 'republic of korea', 'korea', '한국', '韓国', '韩国'],
  }, // 서울
  JP: {
    lat: 35.6762,
    lon: 139.6503,
    names: ['일본', 'japan', '日本', 'ジャパン'],
  }, // 도쿄
  MO: {
    lat: 22.1987,
    lon: 113.5439,
    names: ['마카오', 'macau', 'macao', 'マカオ', '澳门'],
  }, // 마카오
  CN: {
    lat: 39.9042,
    lon: 116.4074,
    names: ['중국', 'china', '中国', '中國'],
  }, // 베이징
  TW: {
    lat: 25.033,
    lon: 121.5654,
    names: ['대만', 'taiwan', '台湾', '台灣', '타이완'],
  }, // 타이베이
  HK: {
    lat: 22.3193,
    lon: 114.1694,
    names: ['홍콩', 'hong kong', '香港'],
  },
  SG: {
    lat: 1.3521,
    lon: 103.8198,
    names: ['싱가포르', 'singapore', 'シンガポール', '新加坡'],
  },
  MY: {
    lat: 3.1408,
    lon: 101.6932,
    names: ['말레이시아', 'malaysia', 'マレーシア', '马来西亚', '쿠알라룸푸르', 'kuala lumpur'],
  }, // 쿠알라룸푸르
  TH: {
    lat: 13.7563,
    lon: 100.5018,
    names: ['태국', 'thailand', 'タイ', '泰国', '방콕', 'bangkok'],
  }, // 방콕
  VN: {
    lat: 21.0285,
    lon: 105.8542,
    names: ['베트남', 'vietnam', 'ベトナム', '越南', '하노이', 'hanoi', '호치민', 'ho chi minh'],
  }, // 하노이
  PH: {
    lat: 14.5995,
    lon: 120.9842,
    names: ['필리핀', 'philippines', 'フィリピン', '菲律宾', '마닐라', 'manila'],
  }, // 마닐라
  ID: {
    lat: -6.2088,
    lon: 106.8456,
    names: ['인도네시아', 'indonesia', 'インドネシア', '印度尼西亚', '자카르타', 'jakarta'],
  }, // 자카르타
  IN: {
    lat: 28.6139,
    lon: 77.209,
    names: ['인도', 'india', 'インド', '印度', '뉴델리', 'new delhi', '델리', 'delhi'],
  }, // 뉴델리
  MM: {
    lat: 19.7633,
    lon: 96.0785,
    names: ['미얀마', 'myanmar', 'burma', 'ミャンマー', '缅甸'],
  }, // 네피도
  KH: {
    lat: 11.5564,
    lon: 104.9282,
    names: ['캄보디아', 'cambodia', 'カンボジア', '柬埔寨', '프놈펜', 'phnom penh'],
  }, // 프놈펜
  LA: {
    lat: 17.9757,
    lon: 102.6331,
    names: ['라오스', 'laos', 'ラオス', '老挝', '비엔티안', 'vientiane'],
  }, // 비엔티안
  BD: {
    lat: 23.8103,
    lon: 90.4125,
    names: ['방글라데시', 'bangladesh', 'バングラデシュ', '孟加拉国', '다카', 'dhaka'],
  }, // 다카
  PK: {
    lat: 33.6844,
    lon: 73.0479,
    names: ['파키스탄', 'pakistan', 'パキスタン', '巴基斯坦', '이슬라마바드', 'islamabad'],
  }, // 이슬라마바드
  LK: {
    lat: 6.9271,
    lon: 79.8612,
    names: ['스리랑카', 'sri lanka', 'スリランカ', '斯里兰卡', '콜롬보', 'colombo'],
  }, // 콜롬보
  NP: {
    lat: 27.7172,
    lon: 85.324,
    names: ['네팔', 'nepal', 'ネパール', '尼泊尔', '카트만두', 'kathmandu'],
  }, // 카트만두
  MN: {
    lat: 47.8864,
    lon: 106.9057,
    names: ['몽골', 'mongolia', 'モンゴル', '蒙古', '울란바토르', 'ulaanbaatar'],
  }, // 울란바토르

  // 중동
  AE: {
    lat: 24.4539,
    lon: 54.3773,
    names: [
      '아랍에미리트',
      'united arab emirates',
      'uae',
      'アラブ首長国連邦',
      '阿联酋',
      '두바이',
      'dubai',
      '아부다비',
      'abu dhabi',
    ],
  }, // 아부다비
  SA: {
    lat: 24.7136,
    lon: 46.6753,
    names: ['사우디아라비아', 'saudi arabia', 'サウジアラビア', '沙特阿拉伯', '리야드', 'riyadh'],
  }, // 리야드
  IL: {
    lat: 31.7683,
    lon: 35.2137,
    names: ['이스라엘', 'israel', 'イスラエル', '以色列', '예루살렘', 'jerusalem', '텔아비브', 'tel aviv'],
  }, // 예루살렘
  TR: {
    lat: 39.9334,
    lon: 32.8597,
    names: ['튀르키예', 'turkey', 'トルコ', '土耳其', '터키', '앙카라', 'ankara', '이스탄불', 'istanbul'],
  }, // 앙카라
  QA: {
    lat: 25.2854,
    lon: 51.531,
    names: ['카타르', 'qatar', 'カタール', '卡塔尔', '도하', 'doha'],
  }, // 도하
  KW: {
    lat: 29.3759,
    lon: 47.9774,
    names: ['쿠웨이트', 'kuwait', 'クウェート', '科威特'],
  }, // 쿠웨이트시티
  OM: {
    lat: 23.588,
    lon: 58.3829,
    names: ['오만', 'oman', 'オマーン', '阿曼', '무스카트', 'muscat'],
  }, // 무스카트
  BH: {
    lat: 26.0667,
    lon: 50.5577,
    names: ['바레인', 'bahrain', 'バーレーン', '巴林', '마나마', 'manama'],
  }, // 마나마
  IR: {
    lat: 35.6892,
    lon: 51.389,
    names: ['이란', 'iran', 'イラン', '伊朗', '테헤란', 'tehran'],
  }, // 테헤란
  IQ: {
    lat: 33.3152,
    lon: 44.3661,
    names: ['이라크', 'iraq', 'イラク', '伊拉克', '바그다드', 'baghdad'],
  }, // 바그다드
  JO: {
    lat: 31.9454,
    lon: 35.9284,
    names: ['요르단', 'jordan', 'ヨルダン', '约旦', '암만', 'amman'],
  }, // 암만

  // 북미
  US: {
    lat: 38.9072,
    lon: -77.0369,
    names: [
      '미국',
      'united states',
      'usa',
      'america',
      'アメリカ',
      '美国',
      '워싱턴',
      'washington',
      '뉴욕',
      'new york',
      '로스앤젤레스',
      'los angeles',
      'la',
    ],
  }, // 워싱턴 DC
  CA: {
    lat: 45.4215,
    lon: -75.6972,
    names: ['캐나다', 'canada', 'カナダ', '加拿大', '오타와', 'ottawa', '토론토', 'toronto', '밴쿠버', 'vancouver'],
  }, // 오타와
  MX: {
    lat: 19.4326,
    lon: -99.1332,
    names: ['멕시코', 'mexico', 'メキシコ', '墨西哥', '멕시코시티', 'mexico city'],
  }, // 멕시코시티

  // 유럽
  GB: {
    lat: 51.5074,
    lon: -0.1278,
    names: ['영국', 'united kingdom', 'uk', 'イギリス', '英国', '런던', 'london'],
  }, // 런던
  DE: {
    lat: 52.52,
    lon: 13.405,
    names: ['독일', 'germany', 'ドイツ', '德国', '베를린', 'berlin', '뮌헨', 'munich', '프랑크푸르트', 'frankfurt'],
  }, // 베를린
  FR: {
    lat: 48.8566,
    lon: 2.3522,
    names: ['프랑스', 'france', 'フランス', '法国', '파리', 'paris'],
  }, // 파리
  IT: {
    lat: 41.9028,
    lon: 12.4964,
    names: ['이탈리아', 'italy', 'イタリア', '意大利', '로마', 'rome', '밀라노', 'milan'],
  }, // 로마
  ES: {
    lat: 40.4168,
    lon: -3.7038,
    names: ['스페인', 'spain', 'スペイン', '西班牙', '마드리드', 'madrid', '바르셀로나', 'barcelona'],
  }, // 마드리드
  NL: {
    lat: 52.3676,
    lon: 4.9041,
    names: ['네덜란드', 'netherlands', 'オランダ', '荷兰', '암스테르담', 'amsterdam'],
  }, // 암스테르담
  BE: {
    lat: 50.8503,
    lon: 4.3517,
    names: ['벨기에', 'belgium', 'ベルギー', '比利时', '브뤼셀', 'brussels'],
  }, // 브뤼셀
  CH: {
    lat: 46.948,
    lon: 7.4474,
    names: ['스위스', 'switzerland', 'スイス', '瑞士', '베른', 'bern', '취리히', 'zurich', '제네바', 'geneva'],
  }, // 베른
  AT: {
    lat: 48.2082,
    lon: 16.3738,
    names: ['오스트리아', 'austria', 'オーストリア', '奥地利', '빈', 'vienna'],
  }, // 빈
  SE: {
    lat: 59.3293,
    lon: 18.0686,
    names: ['스웨덴', 'sweden', 'スウェーデン', '瑞典', '스톡홀름', 'stockholm'],
  }, // 스톡홀름
  NO: {
    lat: 59.9139,
    lon: 10.7522,
    names: ['노르웨이', 'norway', 'ノルウェー', '挪威', '오슬로', 'oslo'],
  }, // 오슬로
  DK: {
    lat: 55.6761,
    lon: 12.5683,
    names: ['덴마크', 'denmark', 'デンマーク', '丹麦', '코펜하겐', 'copenhagen'],
  }, // 코펜하겐
  FI: {
    lat: 60.1699,
    lon: 24.9384,
    names: ['핀란드', 'finland', 'フィンランド', '芬兰', '헬싱키', 'helsinki'],
  }, // 헬싱키
  PL: {
    lat: 52.2297,
    lon: 21.0122,
    names: ['폴란드', 'poland', 'ポーランド', '波兰', '바르샤바', 'warsaw'],
  }, // 바르샤바
  CZ: {
    lat: 50.0755,
    lon: 14.4378,
    names: ['체코', 'czech republic', 'czech', 'チェコ', '捷克', '프라하', 'prague'],
  }, // 프라하
  PT: {
    lat: 38.7223,
    lon: -9.1393,
    names: ['포르투갈', 'portugal', 'ポルトガル', '葡萄牙', '리스본', 'lisbon'],
  }, // 리스본
  GR: {
    lat: 37.9838,
    lon: 23.7275,
    names: ['그리스', 'greece', 'ギリシャ', '希腊', '아테네', 'athens'],
  }, // 아테네
  HU: {
    lat: 47.4979,
    lon: 19.0402,
    names: ['헝가리', 'hungary', 'ハンガリー', '匈牙利', '부다페스트', 'budapest'],
  }, // 부다페스트
  RO: {
    lat: 44.4268,
    lon: 26.1025,
    names: ['루마니아', 'romania', 'ルーマニア', '罗马尼亚', '부쿠레슈티', 'bucharest'],
  }, // 부쿠레슈티
  IE: {
    lat: 53.3498,
    lon: -6.2603,
    names: ['아일랜드', 'ireland', 'アイルランド', '爱尔兰', '더블린', 'dublin'],
  }, // 더블린
  RU: {
    lat: 55.7558,
    lon: 37.6173,
    names: ['러시아', 'russia', 'ロシア', '俄罗斯', '모스크바', 'moscow'],
  }, // 모스크바
  UA: {
    lat: 50.4501,
    lon: 30.5234,
    names: ['우크라이나', 'ukraine', 'ウクライナ', '乌克兰', '키이우', 'kyiv', '키예프', 'kiev'],
  }, // 키이우

  // 오세아니아
  AU: {
    lat: -35.2809,
    lon: 149.13,
    names: [
      '호주',
      'australia',
      'オーストラリア',
      '澳大利亚',
      '캔버라',
      'canberra',
      '시드니',
      'sydney',
      '멜버른',
      'melbourne',
    ],
  }, // 캔버라
  NZ: {
    lat: -41.2865,
    lon: 174.7762,
    names: ['뉴질랜드', 'new zealand', 'ニュージーランド', '新西兰', '웰링턴', 'wellington', '오클랜드', 'auckland'],
  }, // 웰링턴

  // 남미
  BR: {
    lat: -15.8267,
    lon: -47.9218,
    names: [
      '브라질',
      'brazil',
      'ブラジル',
      '巴西',
      '브라질리아',
      'brasilia',
      '상파울루',
      'sao paulo',
      '리우',
      'rio de janeiro',
    ],
  }, // 브라질리아
  AR: {
    lat: -34.6037,
    lon: -58.3816,
    names: ['아르헨티나', 'argentina', 'アルゼンチン', '阿根廷', '부에노스아이레스', 'buenos aires'],
  }, // 부에노스아이레스
  CL: {
    lat: -33.4489,
    lon: -70.6693,
    names: ['칠레', 'chile', 'チリ', '智利', '산티아고', 'santiago'],
  }, // 산티아고
  CO: {
    lat: 4.711,
    lon: -74.0721,
    names: ['콜롬비아', 'colombia', 'コロンビア', '哥伦比亚', '보고타', 'bogota'],
  }, // 보고타
  PE: {
    lat: -12.0464,
    lon: -77.0428,
    names: ['페루', 'peru', 'ペルー', '秘鲁', '리마', 'lima'],
  }, // 리마
  VE: {
    lat: 10.4806,
    lon: -66.9036,
    names: ['베네수엘라', 'venezuela', 'ベネズエラ', '委内瑞拉', '카라카스', 'caracas'],
  }, // 카라카스

  // 아프리카
  ZA: {
    lat: -25.7479,
    lon: 28.2293,
    names: [
      '남아프리카공화국',
      '남아공',
      'south africa',
      '南アフリカ',
      '南非',
      '프리토리아',
      'pretoria',
      '요하네스버그',
      'johannesburg',
    ],
  }, // 프리토리아
  EG: {
    lat: 30.0444,
    lon: 31.2357,
    names: ['이집트', 'egypt', 'エジプト', '埃及', '카이로', 'cairo'],
  }, // 카이로
  MA: {
    lat: 34.0209,
    lon: -6.8416,
    names: ['모로코', 'morocco', 'モロッコ', '摩洛哥', '라바트', 'rabat', '카사블랑카', 'casablanca'],
  }, // 라바트
  NG: {
    lat: 9.0765,
    lon: 7.3986,
    names: ['나이지리아', 'nigeria', 'ナイジェリア', '尼日利亚', '아부자', 'abuja', '라고스', 'lagos'],
  }, // 아부자
  KE: {
    lat: -1.2921,
    lon: 36.8219,
    names: ['케냐', 'kenya', 'ケニア', '肯尼亚', '나이로비', 'nairobi'],
  }, // 나이로비
  ET: {
    lat: 9.0054,
    lon: 38.7636,
    names: ['에티오피아', 'ethiopia', 'エチオピア', '埃塞俄比亚', '아디스아바바', 'addis ababa'],
  }, // 아디스아바바
  TZ: {
    lat: -6.1659,
    lon: 35.7516,
    names: ['탄자니아', 'tanzania', 'タンザニア', '坦桑尼亚', '도도마', 'dodoma'],
  }, // 도도마
  GH: {
    lat: 5.6037,
    lon: -0.187,
    names: ['가나', 'ghana', 'ガーナ', '加纳', '아크라', 'accra'],
  }, // 아크라

  // 기타 아시아 (일본 주요 도시 추가 - 자주 사용)
  JP_OSAKA: {
    lat: 34.6937,
    lon: 135.5023,
    names: ['오사카', 'osaka', '大阪'],
  },
  JP_KYOTO: {
    lat: 35.0116,
    lon: 135.7681,
    names: ['교토', 'kyoto', '京都'],
  },
  JP_FUKUOKA: {
    lat: 33.5904,
    lon: 130.4017,
    names: ['후쿠오카', 'fukuoka', '福岡'],
  },
  JP_SAPPORO: {
    lat: 43.0618,
    lon: 141.3545,
    names: ['삿포로', 'sapporo', '札幌'],
  },
  JP_NAGOYA: {
    lat: 35.1815,
    lon: 136.9066,
    names: ['나고야', 'nagoya', '名古屋'],
  },
  JP_YOKOHAMA: {
    lat: 35.4437,
    lon: 139.638,
    names: ['요코하마', 'yokohama', '横浜'],
  },
  JP_KOBE: {
    lat: 34.6901,
    lon: 135.1956,
    names: ['고베', 'kobe', '神戸'],
  },
  JP_OKINAWA: {
    lat: 26.2124,
    lon: 127.6809,
    names: ['오키나와', 'okinawa', '沖縄'],
  },

  // 중국 주요 도시 추가
  CN_SHANGHAI: {
    lat: 31.2304,
    lon: 121.4737,
    names: ['상하이', 'shanghai', '上海'],
  },
  CN_GUANGZHOU: {
    lat: 23.1291,
    lon: 113.2644,
    names: ['광저우', 'guangzhou', '广州', '廣州'],
  },
  CN_SHENZHEN: {
    lat: 22.5431,
    lon: 114.0579,
    names: ['선전', 'shenzhen', '深圳'],
  },
  CN_CHENGDU: {
    lat: 30.5728,
    lon: 104.0668,
    names: ['청두', 'chengdu', '成都'],
  },
  CN_XIAN: {
    lat: 34.3416,
    lon: 108.9398,
    names: ['시안', 'xian', "xi'an", '西安'],
  },
  CN_HANGZHOU: {
    lat: 30.2741,
    lon: 120.1551,
    names: ['항저우', 'hangzhou', '杭州'],
  },
  CN_QINGDAO: {
    lat: 36.0671,
    lon: 120.3826,
    names: ['칭다오', 'qingdao', '青岛', '青島'],
  },
  CN_DALIAN: {
    lat: 38.914,
    lon: 121.6147,
    names: ['다롄', 'dalian', '大连', '大連'],
  },

  // 미국 주요 도시 추가
  US_LA: {
    lat: 34.0522,
    lon: -118.2437,
    names: ['로스앤젤레스', 'los angeles', 'la', '洛杉矶'],
  },
  US_NYC: {
    lat: 40.7128,
    lon: -74.006,
    names: ['뉴욕', 'new york', 'nyc', '纽约'],
  },
  US_SF: {
    lat: 37.7749,
    lon: -122.4194,
    names: ['샌프란시스코', 'san francisco', 'sf', '旧金山'],
  },
  US_CHICAGO: {
    lat: 41.8781,
    lon: -87.6298,
    names: ['시카고', 'chicago', '芝加哥'],
  },
  US_SEATTLE: {
    lat: 47.6062,
    lon: -122.3321,
    names: ['시애틀', 'seattle', '西雅图'],
  },
  US_LASVEGAS: {
    lat: 36.1699,
    lon: -115.1398,
    names: ['라스베이거스', 'las vegas', '拉斯维加斯'],
  },
  US_HAWAII: {
    lat: 21.3069,
    lon: -157.8583,
    names: ['하와이', 'hawaii', 'honolulu', '호놀룰루', '夏威夷'],
  },
};

// 🌏 한국 시/군/구 중심 좌표 데이터 (Haversine 계산용)
// 🔥 [수정] 2단계 선택 UI를 위해 center + districts 구조로 변경
const DISTRICT_COORDINATES = {
  서울특별시: {
    center: [37.5665, 126.978], // 시청 기준
    districts: {
      강남구: [37.5172, 127.0473],
      강동구: [37.5301, 127.1237],
      강북구: [37.6396, 127.0257],
      강서구: [37.5509, 126.8497],
      관악구: [37.4784, 126.9516],
      광진구: [37.5385, 127.0823],
      구로구: [37.4954, 126.8874],
      금천구: [37.4568, 126.8954],
      노원구: [37.6542, 127.0568],
      도봉구: [37.6688, 127.0471],
      동대문구: [37.5744, 127.04],
      동작구: [37.5124, 126.9393],
      마포구: [37.5638, 126.9084],
      서대문구: [37.5791, 126.9368],
      서초구: [37.4837, 127.0324],
      성동구: [37.5633, 127.0371],
      성북구: [37.5891, 127.0182],
      송파구: [37.5145, 127.1066],
      양천구: [37.5169, 126.866],
      영등포구: [37.5264, 126.8962],
      용산구: [37.5326, 126.99],
      은평구: [37.6027, 126.9291],
      종로구: [37.573, 126.9794],
      중구: [37.5641, 126.997],
      중랑구: [37.6063, 127.0926],
    },
  },
  부산광역시: {
    center: [35.1796, 129.0756], // 시청 기준
    districts: {
      강서구: [35.2122, 128.9806],
      금정구: [35.243, 129.0921],
      기장군: [35.2446, 129.2223],
      남구: [35.1366, 129.0844],
      동구: [35.1294, 129.0456],
      동래구: [35.2046, 129.0747],
      부산진구: [35.1631, 129.0532],
      북구: [35.197, 128.9905],
      사상구: [35.1526, 128.9914],
      사하구: [35.1041, 128.9751],
      서구: [35.0979, 129.0245],
      수영구: [35.1456, 129.1131],
      연제구: [35.1765, 129.0797],
      영도구: [35.0912, 129.0679],
      중구: [35.1062, 129.0324],
      해운대구: [35.1631, 129.1636],
    },
  },
  경기도: {
    center: [37.2752, 127.0095], // 수원 중심
    districts: {
      가평군: [37.8315, 127.5095],
      고양시: [37.6584, 126.832],
      과천시: [37.4292, 126.9876],
      광명시: [37.4786, 126.8645],
      광주시: [37.4294, 127.2551],
      구리시: [37.5943, 127.1295],
      군포시: [37.3614, 126.935],
      김포시: [37.6152, 126.7156],
      남양주시: [37.636, 127.2165],
      동두천시: [37.9035, 127.0605],
      부천시: [37.5034, 126.766],
      성남시: [37.42, 127.1265],
      수원시: [37.2636, 127.0286],
      시흥시: [37.38, 126.8028],
      안산시: [37.3219, 126.8309],
      안성시: [37.0079, 127.2798],
      안양시: [37.3943, 126.9568],
      양주시: [37.785, 127.0456],
      양평군: [37.4917, 127.4876],
      여주시: [37.2983, 127.6375],
      연천군: [38.0966, 127.0748],
      오산시: [37.1498, 127.0694],
      용인시: [37.2411, 127.1776],
      의왕시: [37.3445, 126.9688],
      의정부시: [37.7381, 127.0337],
      이천시: [37.2719, 127.4348],
      파주시: [37.7598, 126.7801],
      평택시: [36.9921, 127.1125],
      포천시: [37.8949, 127.2003],
      하남시: [37.5393, 127.2147],
      화성시: [37.1996, 126.8312],
    },
  },
  인천광역시: {
    center: [37.4563, 126.7052], // 시청 기준
    districts: {
      강화군: [37.747, 126.4878],
      계양구: [37.5372, 126.7376],
      남동구: [37.4488, 126.7309],
      동구: [37.4737, 126.6432],
      미추홀구: [37.4635, 126.6502],
      부평구: [37.507, 126.7219],
      서구: [37.5456, 126.676],
      연수구: [37.4101, 126.678],
      옹진군: [37.4467, 126.6367],
      중구: [37.4835, 126.5556],
    },
  },
  대구광역시: {
    center: [35.8714, 128.6014], // 시청 기준
    districts: {
      남구: [35.8458, 128.5976],
      달서구: [35.8299, 128.5327],
      달성군: [35.7745, 128.4314],
      동구: [35.8863, 128.6357],
      북구: [35.8857, 128.5828],
      서구: [35.8718, 128.5592],
      수성구: [35.8583, 128.6309],
      중구: [35.8693, 128.6062],
    },
  },
  광주광역시: {
    center: [35.1595, 126.8526], // 시청 기준
    districts: {
      광산구: [35.1396, 126.7936],
      남구: [35.1328, 126.9025],
      동구: [35.1462, 126.9231],
      북구: [35.1746, 126.9122],
      서구: [35.1521, 126.8901],
    },
  },
  대전광역시: {
    center: [36.3504, 127.3845], // 시청 기준
    districts: {
      대덕구: [36.3467, 127.4156],
      동구: [36.312, 127.4548],
      서구: [36.3553, 127.3839],
      유성구: [36.3622, 127.3561],
      중구: [36.3255, 127.4214],
    },
  },
  울산광역시: {
    center: [35.5384, 129.3114], // 시청 기준
    districts: {
      남구: [35.5437, 129.3301],
      동구: [35.5049, 129.4166],
      북구: [35.5822, 129.3615],
      울주군: [35.5224, 129.0995],
      중구: [35.5692, 129.332],
    },
  },
  세종특별자치시: {
    center: [36.4801, 127.289], // 시청 기준
    districts: {
      세종시: [36.4801, 127.289],
    },
  },
  강원특별자치도: {
    center: [37.8228, 128.1555], // 도청 기준
    districts: {
      강릉시: [37.7519, 128.8761],
      고성군: [38.3801, 128.4679],
      동해시: [37.5246, 129.1143],
      삼척시: [37.4498, 129.1652],
      속초시: [38.2071, 128.5918],
      양구군: [38.1102, 127.9898],
      양양군: [38.0754, 128.6189],
      영월군: [37.1837, 128.4617],
      원주시: [37.3422, 127.9202],
      인제군: [38.0698, 128.1707],
      정선군: [37.3809, 128.6609],
      철원군: [38.1467, 127.3133],
      춘천시: [37.8813, 127.73],
      태백시: [37.1641, 128.9858],
      평창군: [37.3704, 128.3905],
      홍천군: [37.6972, 127.8883],
      화천군: [38.1062, 127.7081],
      횡성군: [37.4912, 127.9848],
    },
  },
  충청북도: {
    center: [36.635, 127.4914], // 도청 기준
    districts: {
      괴산군: [36.8154, 127.7866],
      단양군: [36.9846, 128.3655],
      보은군: [36.4893, 127.7296],
      영동군: [36.175, 127.7836],
      옥천군: [36.3062, 127.5714],
      음성군: [36.9401, 127.6907],
      제천시: [37.1327, 128.191],
      증평군: [36.7854, 127.5815],
      진천군: [36.8554, 127.4357],
      청주시: [36.6424, 127.489],
      충주시: [36.9909, 127.9259],
    },
  },
  충청남도: {
    center: [36.6588, 126.6728], // 도청 기준
    districts: {
      계룡시: [36.2745, 127.2488],
      공주시: [36.4465, 127.119],
      금산군: [36.1088, 127.4881],
      논산시: [36.1872, 127.0987],
      당진시: [36.8897, 126.6459],
      보령시: [36.3334, 126.6129],
      부여군: [36.2756, 126.9098],
      서산시: [36.7849, 126.4503],
      서천군: [36.0805, 126.6918],
      아산시: [36.7898, 127.0018],
      예산군: [36.6828, 126.8492],
      천안시: [36.8151, 127.1139],
      청양군: [36.459, 126.8022],
      태안군: [36.7457, 126.2979],
      홍성군: [36.6012, 126.6608],
    },
  },
  전북특별자치도: {
    center: [35.7175, 127.153], // 도청 기준
    districts: {
      고창군: [35.4358, 126.7019],
      군산시: [35.9676, 126.7368],
      김제시: [35.8034, 126.8809],
      남원시: [35.4165, 127.3905],
      무주군: [36.0068, 127.6607],
      부안군: [35.7317, 126.7328],
      순창군: [35.3743, 127.1374],
      완주군: [35.9044, 127.1619],
      익산시: [35.9482, 126.9575],
      임실군: [35.6178, 127.2888],
      장수군: [35.6474, 127.5214],
      전주시: [35.8242, 127.148],
      정읍시: [35.5699, 126.8558],
      진안군: [35.7918, 127.4248],
    },
  },
  전라남도: {
    center: [34.8163, 126.4629], // 도청 기준
    districts: {
      강진군: [34.6419, 126.7673],
      고흥군: [34.6049, 127.2751],
      곡성군: [35.2818, 127.2922],
      광양시: [34.9407, 127.6958],
      구례군: [35.2026, 127.4627],
      나주시: [35.0154, 126.7109],
      담양군: [35.3211, 126.9883],
      목포시: [34.8118, 126.3921],
      무안군: [34.9903, 126.4815],
      보성군: [34.7714, 127.0799],
      순천시: [34.9506, 127.4872],
      신안군: [34.8277, 126.1071],
      여수시: [34.7604, 127.6622],
      영광군: [35.2772, 126.5119],
      영암군: [34.8, 126.6969],
      완도군: [34.3108, 126.755],
      장성군: [35.3018, 126.7847],
      장흥군: [34.6816, 126.9068],
      진도군: [34.4868, 126.2635],
      함평군: [35.0656, 126.5166],
      해남군: [34.5733, 126.5991],
      화순군: [35.0644, 126.9867],
    },
  },
  경상북도: {
    center: [36.576, 128.5056], // 도청 기준
    districts: {
      경산시: [35.825, 128.7416],
      경주시: [35.8562, 129.2247],
      고령군: [35.7262, 128.2628],
      구미시: [36.1195, 128.3446],
      군위군: [36.2428, 128.5728],
      김천시: [36.1398, 128.1136],
      문경시: [36.5868, 128.1866],
      봉화군: [36.8931, 128.7325],
      상주시: [36.4108, 128.159],
      성주군: [35.9191, 128.2831],
      안동시: [36.5684, 128.7296],
      영덕군: [36.415, 129.3659],
      영양군: [36.6672, 129.1124],
      영주시: [36.8057, 128.624],
      영천시: [35.9733, 128.9385],
      예천군: [36.6577, 128.4526],
      울릉군: [37.4841, 130.9057],
      울진군: [36.993, 129.4004],
      의성군: [36.3527, 128.697],
      청도군: [35.6474, 128.7341],
      청송군: [36.4361, 129.0571],
      칠곡군: [35.9955, 128.4018],
      포항시: [36.019, 129.3435],
    },
  },
  경상남도: {
    center: [35.2383, 128.6924], // 도청 기준
    districts: {
      거제시: [34.8806, 128.6211],
      거창군: [35.6867, 127.9094],
      고성군: [34.9726, 128.3225],
      김해시: [35.2285, 128.8893],
      남해군: [34.8374, 127.8924],
      밀양시: [35.5037, 128.7467],
      사천시: [35.0038, 128.0641],
      산청군: [35.4155, 127.8735],
      양산시: [35.335, 129.0373],
      의령군: [35.3222, 128.2617],
      진주시: [35.1799, 128.1076],
      창녕군: [35.5441, 128.4914],
      창원시: [35.2279, 128.6811],
      통영시: [34.8545, 128.433],
      하동군: [35.0669, 127.7513],
      함안군: [35.2726, 128.4065],
      함양군: [35.5201, 127.7251],
      합천군: [35.5665, 128.1659],
    },
  },
  제주특별자치도: {
    center: [33.489, 126.4983], // 도청 기준
    districts: {
      서귀포시: [33.254, 126.5602],
      제주시: [33.4996, 126.5312],
    },
  },
};

// 🗺️ 해외 국가별 BEXCO 거리 (km) - 기존 호환용 (좌표 계산 실패 시 폴백)
const COUNTRY_DISTANCES = {
  대한민국: 0,
  'South Korea': 0,
  일본: 250,
  Japan: 250,
  중국: 1200,
  China: 1200,
  미국: 10500,
  USA: 10500,
  'United States': 10500,
  영국: 9000,
  UK: 9000,
  프랑스: 9300,
  France: 9300,
  독일: 8700,
  Germany: 8700,
  베트남: 3200,
  Vietnam: 3200,
  태국: 3800,
  Thailand: 3800,
  싱가포르: 4500,
  Singapore: 4500,
  호주: 6800,
  Australia: 6800,
  대만: 1500,
  Taiwan: 1500,
  홍콩: 2000,
  'Hong Kong': 2000,
  default: 5000,
};

// =============================================================================
// 🔥🔥🔥 [2025-12-18 추가] 게스트 사용자 지원 헬퍼 함수
// =============================================================================

/**
 * 🔥 현재 사용자 식별자 반환 (user_id 또는 device_id)
 * @returns {Object} { user_id, device_id, isGuest }
 */
function getCurrentIdentifier() {
  const authStore = useAuthStore.getState();
  
  if (authStore.isAuthenticated && authStore.user?.id) {
    return { user_id: authStore.user.id, device_id: null, isGuest: false };
  }
  
  // 게스트인 경우 device_id 사용
  const deviceId = getDeviceId();
  return { user_id: null, device_id: deviceId, isGuest: true };
}

/**
 * 🔥 디바이스 ID 가져오기 또는 생성
 * @returns {string} device_id
 */
function getDeviceId() {
  const STORAGE_KEY = 'carbon_device_id';
  
  let deviceId = localStorage.getItem(STORAGE_KEY);
  
  if (!deviceId) {
    // UUID v4 형식으로 생성
    deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    localStorage.setItem(STORAGE_KEY, deviceId);
  }
  
  return deviceId;
}

// =============================================================================
// 🔥🔥🔥 [2025-12-22 추가] API 응답 정규화 헬퍼
// =============================================================================

/**
 * 🔥 API 응답에서 실제 데이터 추출
 * apiClient는 data를 직접 반환하지만, 일부 응답은 { data: ... } 형태일 수 있음
 * @param {any} response - API 응답
 * @returns {any} 실제 데이터
 */
function extractResponseData(response) {
  // null/undefined 처리
  if (response === null || response === undefined) {
    return null;
  }
  
  // apiClient가 이미 data를 반환하므로 그대로 사용
  // 단, 응답이 { data: ... } 형태인 경우 data 추출
  if (response && typeof response === 'object' && 'data' in response && !('total_carbon_saved' in response)) {
    return response.data;
  }
  
  return response;
}

// =============================================================================
// 2. 서비스 클래스 정의
// =============================================================================

class CarbonService {
  constructor() {
    this.baseURL = '/carbon';
    this.requestTimeout = 10000;
    this.maxRetries = 2;
    this.sentry = this._initSentry();
  }

  // ===== Sentry 초기화 (선택적) =====
  _initSentry() {
    try {
      return null;
    } catch (error) {
      logger.warn('⚠️ Sentry 초기화 실패 (선택사항):', error.message);
      return null;
    }
  }

  // ===== 에러 로깅 =====
  _logError(context, error, extraData = {}) {
    logger.error(`❌ ${context}:`, {
      message: error.message,
      ...extraData,
      timestamp: new Date().toISOString(),
    });

    if (this.sentry) {
      try {
        this.sentry.captureException(error, {
          tags: { context },
          extra: extraData,
        });
      } catch (sentryError) {
        logger.warn('⚠️ Sentry 리포팅 실패:', sentryError.message);
      }
    }
  }

  // ===== authStore 헬퍼 메서드 =====
  _getAuthStore() {
    try {
      return useAuthStore;
    } catch (error) {
      logger.warn('⚠️ authStore 로드 실패:', error.message);
      return null;
    }
  }

  _getCurrentUser() {
    const authStore = this._getAuthStore();
    return authStore?.getState?.()?.user;
  }

  _updateUserData(pointsEarned, carbonSaved) {
    try {
      const authStore = this._getAuthStore();
      const currentUser = this._getCurrentUser();

      if (!currentUser) {
        logger.warn('⚠️ currentUser가 없어 업데이트 불가');
        return false;
      }

      if (!authStore?.getState?.()?.updateUser) {
        logger.warn('⚠️ updateUser 함수가 없어 업데이트 불가');
        return false;
      }

      const newCarbonPoints = (currentUser.carbon_points || 0) + (pointsEarned || 0);
      const newTotalCarbonSaved = (currentUser.total_carbon_saved || 0) + (carbonSaved || 0);

      logger.debug('🔄 authStore 업데이트 중:', {
        이전_포인트: currentUser.carbon_points,
        추가_포인트: pointsEarned,
        새_포인트: newCarbonPoints,
        이전_탄소절약: currentUser.total_carbon_saved,
        추가_탄소절약: carbonSaved,
        새_탄소절약: newTotalCarbonSaved,
      });

      authStore.getState().updateUser({
        carbon_points: newCarbonPoints,
        total_carbon_saved: newTotalCarbonSaved,
      });

      logger.debug('✅ authStore 업데이트 완료');
      return true;
    } catch (error) {
      this._logError('authStore 업데이트', error, {
        pointsEarned,
        carbonSaved,
      });
      return false;
    }
  }

  // ===== 타임아웃이 적용된 API 호출 헬퍼 =====
  async _apiCallWithTimeout(apiCall, timeoutMs = this.requestTimeout) {
    return Promise.race([
      apiCall(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('요청 시간이 초과되었습니다')), timeoutMs)
      ),
    ]);
  }

  // ===== 재시도 로직이 적용된 API 호출 =====
  async _apiCallWithRetry(apiCall, retries = this.maxRetries, endpoint = 'unknown') {
    let lastError;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        if (attempt > 0) {
          logger.debug(`🔄 재시도 ${attempt}/${retries}: ${endpoint}`);
          await new Promise((resolve) =>
            setTimeout(resolve, Math.min(1000 * Math.pow(2, attempt - 1), 5000))
          );
        }

        const result = await this._apiCallWithTimeout(apiCall);

        if (attempt > 0) {
          logger.debug(`✅ 재시도 성공: ${endpoint}`);
        }

        return result;
      } catch (error) {
        lastError = error;
        logger.warn(`⚠️ 시도 ${attempt + 1}/${retries + 1} 실패: ${endpoint}`, error.message);

        if (
          attempt < retries &&
          (error.message.includes('timeout') || error.message.includes('network'))
        ) {
          continue;
        }

        break;
      }
    }

    throw lastError;
  }

  // =============================================================================
  // 3. Haversine 거리 계산
  // =============================================================================

  /**
   * Haversine 공식을 이용한 두 좌표 간 거리 계산 (km)
   * @param {number} lat1 - 출발지 위도
   * @param {number} lon1 - 출발지 경도
   * @param {number} lat2 - 도착지 위도
   * @param {number} lon2 - 도착지 경도
   * @returns {number} 거리 (km)
   */
  _calculateHaversine(lat1, lon1, lat2, lon2) {
    const R = 6371; // 지구 반지름 (km)
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    // 도로 굴곡 보정 (직선거리 * 1.35) - 항공 거리에는 보정 안함
    return distance;
  }

  // =============================================================================
  // 🔥 [업그레이드] 해외 좌표 검색 및 거리 계산 메서드
  // =============================================================================

  /**
   * 🔥 [추가] 해외 위치(국가/도시)의 좌표를 찾아 BEXCO와의 거리를 계산
   * @param {string} input - 사용자가 입력한 국가명 또는 도시명
   * @returns {number} 거리 (km)
   */
  _calculateOverseasDistance(input) {
    if (!input) return 5000; // 기본값

    const normalizedInput = input.toLowerCase().trim();
    let targetCoords = null;
    let matchedName = null;

    // 1. OVERSEAS_COORDINATES에서 국가명/도시명 매칭 시도
    for (const [code, data] of Object.entries(OVERSEAS_COORDINATES)) {
      if (data.names.some((name) => normalizedInput.includes(name.toLowerCase()))) {
        targetCoords = data;
        matchedName = data.names[0];
        logger.debug(`🌍 해외 좌표 매칭 성공: ${input} -> ${matchedName} (${code})`);
        break;
      }
    }

    // 2. 좌표를 찾았다면 Haversine 계산
    if (targetCoords) {
      const distance = this._calculateHaversine(
        targetCoords.lat,
        targetCoords.lon,
        BEXCO_COORDS.lat,
        BEXCO_COORDS.lon
      );
      logger.debug(`📏 해외 거리 계산 완료: ${matchedName} -> BEXCO = ${distance.toFixed(1)}km`);
      return Math.round(distance);
    }

    // 3. 매칭 실패 시 기존 COUNTRY_DISTANCES에서 폴백 시도
    const fallbackDistance = COUNTRY_DISTANCES[input] || COUNTRY_DISTANCES[normalizedInput];
    if (fallbackDistance) {
      logger.debug(`📍 폴백 거리 사용: ${input} = ${fallbackDistance}km`);
      return fallbackDistance;
    }

    // 4. 완전 실패 시 기본값
    logger.warn(`⚠️ 해외 좌표 매칭 실패: ${input}, 기본값 5000km 사용`);
    return 5000;
  }

  // =============================================================================
  // 🔥 [추가] 2단계 선택 UI 지원 메서드
  // =============================================================================

  /**
   * 🔥 [추가] 지원하는 도/시 목록 반환 — tenant 별 데이터셋 분기
   * @returns {string[]} 도/시 (또는 HK 광역구) 이름 배열
   */
  getKoreaRegions() {
    if (getRegionDataset() === 'HK') {
      return Object.keys(HK_DISTRICT_COORDINATES);
    }
    return Object.keys(DISTRICT_COORDINATES);
  }

  /**
   * 🔥 [추가] 특정 도/시의 시/군/구 목록 반환 — tenant 별 데이터셋 분기
   * @param {string} region - 도/시 이름 (예: "서울특별시", "Hong Kong Island")
   * @returns {string[]} 시/군/구 이름 배열 (정렬됨)
   */
  getDistricts(region) {
    if (!region) return [];
    if (getRegionDataset() === 'HK') {
      const hkRegion = HK_DISTRICT_COORDINATES[region];
      return hkRegion ? Object.keys(hkRegion.districts).sort() : [];
    }
    if (!DISTRICT_COORDINATES[region]) return [];
    return Object.keys(DISTRICT_COORDINATES[region].districts).sort();
  }

  /**
   * 🔥 [수정] 상세 주소 기반 거리 계산 (2단계 선택 UI용 + 해외 좌표 지원)
   * tenant 에 따라 코디네이트 데이터셋과 venue 좌표가 달라진다.
   * @param {string} regionName - 도/시 이름 또는 'OVERSEAS'
   * @param {string} districtName - 시/군/구 이름 또는 해외 국가/도시명
   * @returns {number} 행사장까지의 거리 (km)
   */
  calculateDistanceByLocation(regionName, districtName = null) {
    // 1. 해외 처리 (좌표 기반 계산 적용)
    if (
      regionName === 'OVERSEAS' ||
      regionName === '해외' ||
      regionName === 'overseas'
    ) {
      return this._calculateOverseasDistance(districtName);
    }

    const dataset = getRegionDataset();
    const dataSource = dataset === 'HK' ? HK_DISTRICT_COORDINATES : DISTRICT_COORDINATES;

    // 2. 국내 지역 데이터 확인
    const regionData = dataSource[regionName];
    if (!regionData) {
      logger.warn(`⚠️ 알 수 없는 지역(${dataset}): ${regionName}, 기본값 200km 사용`);
      return 200;
    }

    let startCoords;
    if (districtName && regionData.districts[districtName]) {
      startCoords = regionData.districts[districtName];
    } else {
      startCoords = regionData.center;
    }

    const directDistance = this._calculateHaversine(
      startCoords[0],
      startCoords[1],
      BEXCO_COORDS.lat,
      BEXCO_COORDS.lon,
    );

    // 도로 보정 — HK 는 좁은 도시국가라 직선거리에 가까운 1.2x, KR 은 1.35x
    const roadFactor = dataset === 'HK' ? 1.2 : 1.35;
    return Math.max(1, Math.round(directDistance * roadFactor));
  }

  // =============================================================================
  // 4. 기존 설문 기반 거리 계산 (호환성 유지 + 해외 좌표 업그레이드)
  // =============================================================================

  /**
   * 🔥 [수정] 설문 응답 기반 거리 계산 (프론트엔드 로컬 계산 + 해외 좌표 지원)
   * @param {string} region - 지역 구분 (ex: 'busan', 'seoul_gyeonggi', 'overseas')
   * @param {string} specificLocation - 세부 위치 (ex: '해운대구', '강남구', '일본', '오사카')
   * @returns {number} 거리 (km)
   */
  calculateDistanceFromSurvey(region, specificLocation = null) {
    // 1. 해외인 경우 (좌표 로직 적용)
    if (region === 'overseas' || region === 'OVERSEAS') {
      // specificLocation이 배열([국가, 도시])이거나 문자열일 수 있음
      const locationStr = Array.isArray(specificLocation)
        ? specificLocation.join(' ')
        : specificLocation;
      return this._calculateOverseasDistance(locationStr);
    }

    // 2. 부산 내부인 경우 (BEXCO와의 거리)
    if (region === 'busan') {
      if (
        specificLocation &&
        DISTRICT_COORDINATES['부산광역시']?.districts?.[specificLocation]
      ) {
        const [lat, lon] = DISTRICT_COORDINATES['부산광역시'].districts[specificLocation];
        const distance = this._calculateHaversine(lat, lon, BEXCO_COORDS.lat, BEXCO_COORDS.lon);
        return Math.max(2, Math.round(distance * 1.35)); // 최소 2km, 도로 보정 적용
      }
      return 10; // 부산 평균
    }

    // 3. 국내 타 지역 (대표 좌표 사용)
    const regionCoords = {
      seoul_gyeonggi: DISTRICT_COORDINATES['서울특별시']?.center,
      seoul: DISTRICT_COORDINATES['서울특별시']?.center,
      gyeonggi: DISTRICT_COORDINATES['경기도']?.center,
      gyeongsang: DISTRICT_COORDINATES['대구광역시']?.center,
      daegu: DISTRICT_COORDINATES['대구광역시']?.center,
      chungcheong: DISTRICT_COORDINATES['대전광역시']?.center,
      daejeon: DISTRICT_COORDINATES['대전광역시']?.center,
      jeolla: DISTRICT_COORDINATES['광주광역시']?.center,
      gwangju: DISTRICT_COORDINATES['광주광역시']?.center,
      gangwon: DISTRICT_COORDINATES['강원특별자치도']?.center,
      jeju: DISTRICT_COORDINATES['제주특별자치도']?.center,
      incheon: DISTRICT_COORDINATES['인천광역시']?.center,
      ulsan: DISTRICT_COORDINATES['울산광역시']?.center,
      sejong: DISTRICT_COORDINATES['세종특별자치시']?.center,
    };

    // specificLocation이 시/도/구 이름인 경우 좌표 찾기
    if (specificLocation) {
      for (const [cityName, cityData] of Object.entries(DISTRICT_COORDINATES)) {
        if (cityData.districts && cityData.districts[specificLocation]) {
          const [lat, lon] = cityData.districts[specificLocation];
          const distance = this._calculateHaversine(lat, lon, BEXCO_COORDS.lat, BEXCO_COORDS.lon);
          return Math.round(distance * 1.35); // 도로 보정 적용
        }
      }
    }

    const coords = regionCoords[region];
    if (coords) {
      const [lat, lon] = Array.isArray(coords) ? coords : [coords[0], coords[1]];
      const distance = this._calculateHaversine(lat, lon, BEXCO_COORDS.lat, BEXCO_COORDS.lon);
      return Math.round(distance * 1.35); // 도로 보정 적용
    }

    return 200; // 기본값
  }

  /**
   * 탄소 감축량 계산
   * @param {number} distanceKm - 이동 거리
   * @param {string} transportType - 교통수단 ID
   * @param {object} ctx - 컨텍스트 (origin 등). 항공편 도메스틱/인터내셔널 분기에 사용.
   *                      ctx.isDomestic === true 면 단거리, false 면 장거리.
   *                      미지정 시 distanceKm < 1500 이면 도메스틱으로 추정.
   * @returns {Object} 계산 결과
   */
  calculateCarbonReduction(distanceKm, transportType, ctx = {}) {
    // 배출 계수 매핑 (설문 ID -> 로직 ID).
    // HK 전용 ID (mtr, hk_tram, hk_bus 등) 도 1:1 매핑.
    const transportMap = {
      walk_bicycle: 'walk_bike',
      walk: 'walk',
      bicycle: 'bicycle',
      public: 'subway',
      public_transit: 'subway',
      public_bus: 'public_bus',
      subway: 'subway',
      bus: 'bus',
      train: 'train',
      ktx: 'train_ktx',
      car_internal: 'car_normal',
      car: 'car_normal',
      car_gasoline: 'car_gasoline',
      car_diesel: 'car_diesel',
      car_electric: 'car_electric',
      electric_car: 'car_electric',
      car_hybrid: 'car_hybrid',
      airplane: 'airplane',
      plane: 'airplane',
      taxi: 'taxi',
      motorcycle: 'motorcycle',
      // 🇭🇰 HK 전용
      mtr: 'mtr',
      light_rail: 'light_rail',
      hk_tram: 'hk_tram',
      peak_tram: 'peak_tram',
      hk_bus: 'hk_bus',
      minibus: 'minibus',
      ferry: 'ferry',
      airport_express: 'airport_express',
    };

    let logicType = transportMap[transportType] || 'other';

    // ✈️ 항공편 도메스틱/인터내셔널 자동 분기.
    //   - ctx.isDomestic 가 명시되면 그 값을 우선
    //   - 그렇지 않으면 distance 임계 (1500km) 로 추정
    //   home country 동일 → domestic, 외국 → international 로 호출자에서
    //   ctx.isDomestic 을 결정해 넘기는 것이 정석.
    if (logicType === 'airplane' || logicType === 'plane') {
      let isDomestic = ctx.isDomestic;
      if (typeof isDomestic !== 'boolean') {
        isDomestic = Number(distanceKm) < 1500;
      }
      logicType = isDomestic ? 'airplane_domestic' : 'airplane_international';
    }

    const factor = EMISSION_FACTORS_GRAMS[logicType] || 80;

    // 배출량 계산 (g 단위)
    const emissionG = distanceKm * factor;
    const baselineG = distanceKm * BASELINE_EMISSION_GRAMS;

    // 감축량 계산 (기준 - 실제)
    let reductionG = baselineG - emissionG;

    // 비행기 등 기준보다 많이 배출하는 경우 플래그 처리
    const isNegative = reductionG < 0;
    const reductionKg = reductionG / 1000;
    const emissionKg = emissionG / 1000;

    // 환산값 (나무, 전구, 컵)
    const equivalents = {
      trees: (Math.abs(reductionG) / (22000 / 365)).toFixed(1), // 나무 1그루 일일 흡수량 기준
      light_hours: (Math.abs(reductionG) / 10).toFixed(0), // 10g/h
      cups: (Math.abs(reductionG) / 25).toFixed(0), // 25g/cup
    };

    return {
      distanceKm: parseFloat(distanceKm.toFixed(1)),
      emissionKg: parseFloat(emissionKg.toFixed(2)),
      reductionKg: parseFloat(reductionKg.toFixed(2)),
      reductionG: parseFloat(reductionG.toFixed(0)),
      isNegative,
      equivalents,
      transportType: logicType,
      factor: factor,
    };
  }

  // =============================================================================
  // 🔥🔥🔥 [2025-12-18 수정] 탄소 활동 기록 저장 (백엔드 전송 + 로컬 스토어 업데이트)
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  /**
   * 🔥🔥🔥 탄소 활동 기록 저장 (백엔드 전송 + 로컬 스토어 업데이트)
   * @param {Object} data - 활동 데이터
   * @returns {Promise<Object>} 결과
   */
  async trackCarbonActivity(data) {
    const endpoint = `${this.baseURL}/track`;
    
    try {
      const { user_id, device_id, isGuest } = getCurrentIdentifier();
      
      // 🔥 게스트인 경우 device_id를 payload에 포함
      const payload = {
        ...data,
        device_id: isGuest ? device_id : null
      };
      
      logger.debug(`🚗 탄소 활동 기록 (${isGuest ? '게스트' : '로그인'}):`, payload);
      
      const response = await this._apiCallWithRetry(
        () => apiClient.post(endpoint, payload),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환하므로 response 자체가 data
      const responseData = extractResponseData(response);
      
      // 🔥 [핵심] authStore 상태 즉시 업데이트 (서버 응답값 사용)
      const savedAmount = responseData?.carbon_saved !== undefined 
                          ? parseFloat(responseData.carbon_saved) 
                          : parseFloat(data.saved_co2 || 0);
                          
      const pointsAmount = responseData?.points_earned || 0;
      
      // 로그인 사용자만 authStore 업데이트
      if (!isGuest) {
        this._updateUserData(pointsAmount, savedAmount);
      }
      
      return responseData;
    } catch (error) {
      logger.error('Track carbon error:', error);
      // 오프라인이거나 백엔드 미구현 시에도 성공으로 간주 (Demo용)
      return { success: true, local: true };
    }
  }

  // =============================================================================
  // 🔥🔥🔥 [2025-12-18 수정] 대시보드 통계 조회
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  /**
   * 🔥🔥🔥 대시보드 통계 조회
   * 로그인 사용자: user_id 기반
   * 게스트 사용자: device_id 기반
   */
  async getDashboardStats() {
    const endpoint = `${this.baseURL}/dashboard-stats`;
    
    try {
      const { user_id, device_id, isGuest } = getCurrentIdentifier();
      
      let url = endpoint;
      
      // 🔥 게스트인 경우 device_id를 쿼리 파라미터로 전달
      if (isGuest && device_id) {
        url += `?device_id=${encodeURIComponent(device_id)}`;
      }
      
      logger.debug(`📊 대시보드 통계 API 호출 (${isGuest ? '게스트' : '로그인'}):`, url);
      
      const response = await this._apiCallWithRetry(
        () => apiClient.get(url),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);
      
      logger.debug('📊 [CarbonService] Dashboard 통계 응답:', responseData);
      
      return responseData;
    } catch (error) {
      this._logError('대시보드 통계 조회', error, {
        endpoint,
        status: error.response?.status,
      });
      
      // 🔥 [수정] 더미 데이터 제거 및 0으로 초기화
      let fallbackCarbonPoints = 0;
      let fallbackTotalCarbonSaved = 0;
      
      const currentUser = this._getCurrentUser();
      if (currentUser) {
        fallbackCarbonPoints = currentUser.carbon_points || 0;
        fallbackTotalCarbonSaved = currentUser.total_carbon_saved || 0;
      }
      
      const emptyData = {
        carbon_points: fallbackCarbonPoints,
        total_carbon_saved: fallbackTotalCarbonSaved,
        total_activities: 0,
        this_week_saved: 0,
        this_month_saved: 0,
        monthly_goal: 50,
        monthly_progress: 0,
        rank: null,
        percentile: null,
        is_guest: true
      };
      
      logger.debug('📋 데이터 없음/에러: 0으로 초기화된 데이터 반환', emptyData);
      return emptyData;
    }
  }

  // =============================================================================
  // 6. 탄소 통계 조회 (시각화용)
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  async getCarbonStats(periodDays = 30) {
    const endpoint = `${this.baseURL}/visualization`;
    try {
      logger.debug('📈 탄소 통계 API 호출:', endpoint, { period_days: periodDays });

      const response = await this._apiCallWithRetry(
        () =>
          apiClient.get(endpoint, {
            params: { period_days: periodDays },
          }),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const data = extractResponseData(response);

      const transformedData = {
        totalPoints: data?.user_stats?.total_carbon_saved * 10 || 0,
        totalSaved: data?.user_stats?.total_carbon_saved || 0,
        monthlyGoal: 50,
        currentMonth: data?.user_stats?.total_carbon_saved || 0,
        rank: data?.leaderboard_position?.rank || null,
        totalUsers: data?.leaderboard_position?.total_users || 1,
        weeklyTrends: data?.user_stats?.weekly_trends || [],
        transportationBreakdown: data?.transportation_breakdown || [],
        monthlyTrends: data?.monthly_trends || [],
        achievements: data?.achievements || [],
        environmentalImpact: data?.environmental_impact || {},
        comparisonData: data?.comparison_data || {},
      };

      logger.debug('✅ 탄소 통계 변환 완료:', {
        totalPoints: transformedData.totalPoints,
        totalSaved: transformedData.totalSaved,
        rank: transformedData.rank,
      });

      return transformedData;
    } catch (error) {
      this._logError('탄소 통계 조회', error, {
        endpoint,
        status: error.response?.status,
        periodDays,
      });

      logger.debug('📋 빈 통계 데이터 반환');
      return {
        totalPoints: 0,
        totalSaved: 0,
        monthlyGoal: 50,
        currentMonth: 0,
        rank: null,
        totalUsers: 0,
        weeklyTrends: [],
        transportationBreakdown: [],
        monthlyTrends: [],
        achievements: [],
        environmentalImpact: {},
        comparisonData: {},
      };
    }
  }

  /**
   * 월별 탄소 통계
   * @param {number} year - 연도
   * @param {number} month - 월 (1-12)
   * @returns {Promise<Object>} 월별 통계
   */
  async getMonthlyCarbonStats(year, month) {
    const endpoint = `${this.baseURL}/stats/monthly`;
    try {
      const response = await this._apiCallWithRetry(
        () =>
          apiClient.get(endpoint, {
            params: { year, month },
          }),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      return extractResponseData(response);
    } catch (error) {
      this._logError('월별 탄소 통계 조회', error, { endpoint });
      throw error;
    }
  }

  /**
   * 이벤트별 탄소 통계
   * @param {string|number} eventId - 이벤트 ID
   * @returns {Promise<Object>} 이벤트 탄소 통계
   */
  async getEventCarbonStats(eventId) {
    const endpoint = `${this.baseURL}/stats/event/${eventId}`;
    try {
      const response = await this._apiCallWithRetry(
        () => apiClient.get(endpoint),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      return extractResponseData(response);
    } catch (error) {
      this._logError('이벤트 탄소 통계 조회', error, { endpoint, eventId });
      throw error;
    }
  }

  // =============================================================================
  // 7. 탄소 활동 기록 (기존 API)
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  /**
   * 탄소 활동 기록 (이동 정보 저장)
   * @param {Object} trackingData - 활동 데이터
   * @returns {Promise<Object>} 기록 결과
   */
  async trackCarbon(trackingData) {
    const endpoint = `${this.baseURL}/track`;
    try {
      logger.debug('🚗 탄소발자국 기록 API 호출:', endpoint, {
        transport_mode: trackingData.transportation_type,
        distance: trackingData.distance,
      });

      const response = await this._apiCallWithRetry(
        () => apiClient.post(endpoint, trackingData),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);

      logger.debug('✅ 탄소발자국 기록 성공:', {
        carbon_saved: responseData?.carbon_saved,
        points_earned: responseData?.points_earned,
      });

      this._updateUserData(responseData?.points_earned, responseData?.carbon_saved);

      return responseData;
    } catch (error) {
      this._logError('탄소발자국 기록', error, {
        endpoint,
        status: error.response?.status,
      });
      throw new Error(
        error.response?.data?.detail ||
          '탄소발자국 기록 중 오류가 발생했습니다. 다시 시도해주세요.'
      );
    }
  }

  /**
   * 간단한 활동 기록
   */
  async trackSimpleActivity(activityData) {
    const endpoint = `${this.baseURL}/track/simple`;
    try {
      logger.debug('🌱 활동 기록 API 호출:', endpoint, activityData);

      const response = await this._apiCallWithRetry(
        () =>
          apiClient.post(endpoint, {
            activity_type: activityData.activity_type,
            quantity: activityData.quantity || 1.0,
            duration_minutes: activityData.duration_minutes || null,
            notes: activityData.notes || null,
          }),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);

      logger.debug('✅ 활동 기록 API 성공:', {
        id: responseData?.id,
        carbon_saved: responseData?.carbon_saved,
        points_earned: responseData?.points_earned,
      });

      const updateSuccess = this._updateUserData(
        responseData?.points_earned,
        responseData?.carbon_saved
      );

      if (updateSuccess) {
        logger.debug('✅ authStore 자동 업데이트 성공');
      } else {
        logger.warn('⚠️ authStore 자동 업데이트 실패 (화면에서 수동 갱신 필요)');
      }

      return responseData;
    } catch (error) {
      this._logError('활동 기록', error, {
        endpoint,
        activityData,
        status: error.response?.status,
      });
      throw new Error(
        error.response?.data?.detail ||
          '활동 기록 중 오류가 발생했습니다. 네트워크를 확인해주세요.'
      );
    }
  }

  /**
   * 탄소 활동 인증 (이미지 포함)
   */
  async submitActivity(activityData) {
    const endpoint = `${this.baseURL}/activity`;
    try {
      logger.debug('📸 활동 인증 API 호출:', endpoint, {
        activity_type: activityData.activity_type,
        has_photo: !!activityData.photo,
      });

      const formData = new FormData();
      formData.append('activity_type', activityData.activity_type);
      formData.append('description', activityData.description);

      if (activityData.photo) {
        formData.append('photo', {
          uri: activityData.photo.uri,
          type: activityData.photo.type || 'image/jpeg',
          name: activityData.photo.fileName || 'activity.jpg',
        });
      }

      const response = await this._apiCallWithRetry(
        () =>
          apiClient.post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }),
        1,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);

      logger.debug('✅ 활동 인증 성공:', {
        id: responseData?.id,
        points_earned: responseData?.points_earned,
        status: responseData?.status,
      });

      this._updateUserData(responseData?.points_earned, responseData?.carbon_saved || 0);

      return responseData;
    } catch (error) {
      this._logError('활동 인증', error, {
        endpoint,
        status: error.response?.status,
      });
      throw new Error(
        error.response?.data?.detail ||
          '활동 인증 중 오류가 발생했습니다. 사진과 내용을 확인해주세요.'
      );
    }
  }

  /**
   * 탄소 활동 상세 조회
   * @param {string|number} activityId - 활동 ID
   * @returns {Promise<Object>} 활동 상세
   */
  async getCarbonActivity(activityId) {
    const endpoint = `${this.baseURL}/activities/${activityId}`;
    try {
      const response = await this._apiCallWithRetry(
        () => apiClient.get(endpoint),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      return extractResponseData(response);
    } catch (error) {
      this._logError('탄소 활동 상세 조회', error, { endpoint, activityId });
      throw error;
    }
  }

  // =============================================================================
  // 🔥🔥🔥 [2025-12-18 수정] 탄소 히스토리 조회
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  /**
   * 🔥🔥🔥 탄소 히스토리 조회
   * 게스트 사용자 지원 추가
   */
  async getCarbonHistory(limit = 20, offset = 0) {
    const endpoint = `${this.baseURL}/history`;
    try {
      const { user_id, device_id, isGuest } = getCurrentIdentifier();
      
      let url = `${endpoint}?limit=${limit}&offset=${offset}`;
      
      // 🔥 게스트인 경우 device_id를 쿼리 파라미터로 추가
      if (isGuest && device_id) {
        url += `&device_id=${encodeURIComponent(device_id)}`;
      }
      
      logger.debug(`📋 탄소 히스토리 API 호출 (${isGuest ? '게스트' : '로그인'}):`, url);

      const response = await this._apiCallWithRetry(
        () => apiClient.get(url),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);

      // 🔥 방어 코드 추가: responseData가 없는 경우 처리
      if (!responseData) {
        logger.warn('⚠️ Carbon history response is empty');
        return [];
      }

      // 백엔드 응답 구조가 { history: [] }, { records: [] } 또는 그냥 [] 인지 확인하여 처리
      const history = responseData.history || responseData.records || (Array.isArray(responseData) ? responseData : []);

      // 배열이 아닌 경우 빈 배열 반환
      if (!Array.isArray(history)) {
        logger.warn('⚠️ Carbon history data is not an array:', typeof history);
        return [];
      }

      const formattedHistory = history.map((item) => ({
        id: item.id,
        title: this.getHistoryTitle(item),
        date: new Date(item.created_at || item.date).toLocaleDateString('ko-KR'),
        points: item.points_earned || item.points || 0,
        carbon_saved: item.carbon_saved || 0,
        type: item.activity_type || 'transport',
        details: item.details || item,
      }));

      logger.debug('✅ 탄소 히스토리 조회 성공:', {
        count: formattedHistory.length,
        total: responseData?.total,
        isGuest,
      });

      return formattedHistory;
    } catch (error) {
      this._logError('탄소 히스토리 조회', error, {
        endpoint,
        status: error.response?.status,
      });

      logger.debug('📋 빈 히스토리 반환 (API 실패)');
      return [];
    }
  }

  getHistoryTitle(item) {
    if (item.activity_type === 'transportation') {
      const transportNames = {
        walking: '도보',
        walk: '도보',
        bicycle: '자전거',
        public_transit: '대중교통',
        public_bus: '버스',
        subway: '지하철',
        electric_car: '전기차',
        car_electric: '전기차',
        car: '자동차',
        car_normal: '자동차',
        car_gasoline: '자동차(가솔린)',
        car_diesel: '자동차(디젤)',
        car_hybrid: '자동차(하이브리드)',
        taxi: '택시',
        airplane: '비행기',
        plane: '비행기',
        bus: '버스',
        train: 'KTX/열차',
        motorcycle: '오토바이',
      };

      const transportType = item.transportation_type || item.transport_mode || 'unknown';
      const name = transportNames[transportType] || transportType;
      const distance = item.distance || item.distance_km || 0;

      return `${name} 이용 (${distance}km)`;
    } else {
      const activityNames = {
        tumbler: '텀블러 사용',
        reusable_bag: '장바구니 사용',
        stairs: '계단 이용',
        public_wifi: '공용 와이파이',
        paper_save: '종이 절약',
        energy_save: '전력 절약',
        walking: '걷기',
      };

      return (activityNames[item.activity_type] || '환경 활동') + ' 인증';
    }
  }

  // =============================================================================
  // 🔥🔥🔥 [2025-12-18 신규] 게스트 데이터 마이그레이션
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  /**
   * 🔥🔥🔥 [신규] 게스트 데이터를 로그인 사용자로 마이그레이션
   */
  async migrateGuestData() {
    try {
      const authStore = useAuthStore.getState();
      
      // 로그인 상태가 아니면 마이그레이션 불필요
      if (!authStore.isAuthenticated) {
        logger.debug('[carbonService] 로그인 상태가 아니므로 마이그레이션 스킵');
        return { migrated_count: 0, message: '로그인이 필요합니다' };
      }
      
      const deviceId = getDeviceId();
      
      if (!deviceId) {
        logger.debug('[carbonService] device_id가 없으므로 마이그레이션 스킵');
        return { migrated_count: 0, message: 'device_id가 없습니다' };
      }
      
      logger.debug(`[carbonService] 게스트 데이터 마이그레이션 시작: device_id=${deviceId.substring(0, 8)}...`);
      
      const response = await apiClient.post(`${this.baseURL}/migrate-guest-data`, {
        device_id: deviceId
      });
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const result = extractResponseData(response);
      
      if (result?.migrated_count > 0) {
        logger.debug(`[carbonService] ✅ 마이그레이션 완료: ${result.migrated_count}개 기록, +${result.total_points_added} 포인트, +${result.total_carbon_saved_added}kg 절감`);
        
        // authStore 업데이트 (포인트 반영)
        this._updateUserData(
          result.total_points_added || 0,
          result.total_carbon_saved_added || 0
        );
      } else {
        logger.debug('[carbonService] 마이그레이션할 데이터 없음');
      }
      
      return result;
    } catch (error) {
      logger.error('[carbonService] ❌ 게스트 데이터 마이그레이션 실패:', error);
      // 마이그레이션 실패해도 로그인 프로세스는 계속 진행
      return { migrated_count: 0, error: error.message };
    }
  }

  /**
   * 🔥🔥🔥 [신규] 게스트 통계 조회 (device_id 기반, 인증 불필요)
   */
  async getGuestStats(deviceId = null) {
    try {
      const targetDeviceId = deviceId || getDeviceId();
      
      if (!targetDeviceId) {
        return {
          total_activities: 0,
          total_carbon_saved: 0,
          total_points: 0,
          recent_activities: [],
          is_guest: true,
          can_migrate: false
        };
      }
      
      const response = await apiClient.get(`${this.baseURL}/guest-stats/${targetDeviceId}`);
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      return extractResponseData(response);
    } catch (error) {
      logger.error('[carbonService] 게스트 통계 조회 실패:', error);
      return {
        total_activities: 0,
        total_carbon_saved: 0,
        total_points: 0,
        recent_activities: [],
        is_guest: true,
        can_migrate: false
      };
    }
  }

  // =============================================================================
  // 9. 포인트 관련
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  /**
   * 포인트 잔액 조회
   * @returns {Promise<Object>} 포인트 정보
   */
  async getPointBalance() {
    const endpoint = `${this.baseURL}/points/balance`;
    try {
      const response = await this._apiCallWithRetry(
        () => apiClient.get(endpoint),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      return extractResponseData(response);
    } catch (error) {
      this._logError('포인트 잔액 조회', error, { endpoint });
      throw error;
    }
  }

  /**
   * 포인트 이력 조회
   */
  async getPointHistory(limit = 50, offset = 0) {
    const endpoint = `${this.baseURL}/points/history`;
    try {
      logger.debug('📊 포인트 내역 API 호출:', endpoint, { limit, offset });

      const response = await this._apiCallWithRetry(
        () =>
          apiClient.get(endpoint, {
            params: { limit, offset },
          }),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);
      const historyData = responseData?.history || responseData || [];

      const history = historyData.map((item) => ({
        id: item.id,
        type: item.type,
        amount: item.amount,
        description: item.description,
        date: new Date(item.created_at).toLocaleDateString('ko-KR'),
        details: item.details,
      }));

      logger.debug('✅ 포인트 내역 조회 성공:', { count: history.length });
      return history;
    } catch (error) {
      this._logError('포인트 내역 조회', error, {
        endpoint,
        status: error.response?.status,
      });
      logger.debug('📋 빈 포인트 내역 반환');
      return [];
    }
  }

  /**
   * 포인트 사용
   */
  async usePoints(data) {
    const endpoint = `${this.baseURL}/points/use`;
    try {
      const response = await this._apiCallWithRetry(
        () => apiClient.post(endpoint, data),
        1,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);
      this._updateUserData(-(data.amount || 0), 0);
      return responseData;
    } catch (error) {
      this._logError('포인트 사용', error, { endpoint });
      throw error;
    }
  }

  // =============================================================================
  // 10. 랭킹/리더보드
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  /**
   * 탄소 절감 랭킹 조회
   */
  async getCarbonRanking(params = {}) {
    const endpoint = `${this.baseURL}/ranking`;
    try {
      const response = await this._apiCallWithRetry(
        () => apiClient.get(endpoint, { params }),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      return extractResponseData(response);
    } catch (error) {
      this._logError('탄소 랭킹 조회', error, { endpoint });
      throw error;
    }
  }

  /**
   * 내 랭킹 조회
   */
  async getMyRanking() {
    const endpoint = `${this.baseURL}/ranking/me`;
    try {
      const response = await this._apiCallWithRetry(
        () => apiClient.get(endpoint),
        this.maxRetries,
        endpoint
      );
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      return extractResponseData(response);
    } catch (error) {
      this._logError('내 랭킹 조회', error, { endpoint });
      throw error;
    }
  }

  /**
   * 리더보드 조회
   */
  async getLeaderboard(period = 'monthly', limit = 50) {
    const endpoint = `${this.baseURL}/leaderboard`;
    try {
      logger.debug('🏆 리더보드 API 호출:', endpoint, { period, limit });

      const response = await this._apiCallWithRetry(
        () =>
          apiClient.get(endpoint, {
            params: { period, limit },
          }),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);

      logger.debug('✅ 리더보드 조회 성공:', {
        count: responseData?.leaderboard?.length || 0,
        total_participants: responseData?.total_participants,
      });

      return responseData;
    } catch (error) {
      this._logError('리더보드 조회', error, {
        endpoint,
        status: error.response?.status,
      });

      logger.debug('📋 빈 리더보드 반환');
      return {
        leaderboard: [],
        period: period,
        total_participants: 0,
        generated_at: new Date().toISOString(),
      };
    }
  }

  // =============================================================================
  // 11. 탄소 목표
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  async getCarbonGoals() {
    const endpoint = `${this.baseURL}/goals`;
    try {
      logger.debug('🎯 탄소 목표 API 호출:', endpoint);

      const response = await this._apiCallWithRetry(
        () => apiClient.get(endpoint),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);
      logger.debug('✅ 탄소 목표 조회 성공');
      return responseData;
    } catch (error) {
      this._logError('탄소 목표 조회', error, {
        endpoint,
        status: error.response?.status,
      });

      logger.debug('📋 빈 목표 데이터 반환');
      return {
        monthly: {
          target: 50.0,
          achieved: 0,
          progress_percentage: 0,
          remaining: 50.0,
          days_left: 30,
        },
        yearly: {
          target: 600.0,
          achieved: 0,
          progress_percentage: 0,
          remaining: 600.0,
          days_left: 365,
        },
        suggestions: [],
      };
    }
  }

  // =============================================================================
  // 12. 기부 관련
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  async getDonationHistory(limit = 20) {
    const endpoint = `${this.baseURL}/donations`;
    try {
      logger.debug('💰 기부 내역 API 호출:', endpoint, { limit });

      const response = await this._apiCallWithRetry(
        () =>
          apiClient.get(endpoint, {
            params: { limit },
          }),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);

      logger.debug('✅ 기부 내역 조회 성공:', {
        count: responseData?.donations?.length || 0,
        total_donated: responseData?.summary?.total_donated,
      });

      return responseData;
    } catch (error) {
      this._logError('기부 내역 조회', error, {
        endpoint,
        status: error.response?.status,
      });

      logger.debug('📋 빈 기부 내역 반환');
      return {
        donations: [],
        summary: {
          total_donated: 0,
          total_carbon_offset: 0,
          donation_count: 0,
          avg_donation: 0,
        },
      };
    }
  }

  async makeDonation(donationData) {
    const endpoint = `${this.baseURL}/donate`;
    try {
      logger.debug('💝 기부 API 호출:', endpoint, {
        amount: donationData.amount,
        purpose: donationData.purpose,
      });

      const response = await this._apiCallWithRetry(
        () => apiClient.post(endpoint, donationData),
        1,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);

      logger.debug('✅ 기부 완료:', {
        id: responseData?.id,
        amount: responseData?.amount,
        receipt_number: responseData?.receipt_number,
      });

      return responseData;
    } catch (error) {
      this._logError('기부', error, {
        endpoint,
        status: error.response?.status,
      });
      throw new Error(
        error.response?.data?.detail ||
          '기부 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
      );
    }
  }

  // =============================================================================
  // 13. 배출 계수 API
  // 🔥🔥🔥 [2025-12-22 수정] apiClient 응답 구조 호환성 수정
  // =============================================================================

  async getEmissionFactors() {
    const endpoint = `${this.baseURL}/emission-factors`;
    try {
      logger.debug('📐 배출 계수 API 호출:', endpoint);

      const response = await this._apiCallWithRetry(
        () => apiClient.get(endpoint),
        this.maxRetries,
        endpoint
      );

      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);
      logger.debug('✅ 배출 계수 조회 성공');
      return responseData;
    } catch (error) {
      this._logError('배출 계수 조회', error, {
        endpoint,
        status: error.response?.status,
      });

      logger.debug('📋 로컬 배출 계수 반환');
      return EMISSION_FACTORS;
    }
  }

  // =============================================================================
  // 14. 유틸리티 (로컬 계산) - 기존 호환
  // =============================================================================

  /**
   * 거리 계산 (로컬) - 도시명으로 BEXCO까지 거리 반환
   */
  calculateDistanceLocal(origin) {
    for (const [region, distance] of Object.entries(DISTANCE_TO_BEXCO)) {
      if (origin.includes(region)) {
        return distance;
      }
    }
    return DISTANCE_TO_BEXCO['기타'] || 200;
  }

  /**
   * 거리 계산 (출발지 → BEXCO) - 기존 메서드 호환
   */
  async calculateDistance(
    startLocation,
    endLocation = 'BEXCO 제1전시장, APEC로 55, 해운대구, 부산'
  ) {
    try {
      logger.debug('📍 거리 계산:', { startLocation, endLocation });
      const distance = this.calculateDistanceLocal(startLocation);
      logger.debug('✅ 거리 계산 완료:', { startLocation, distance });
      return distance;
    } catch (error) {
      logger.error('❌ 거리 계산 실패:', error);
      return 15;
    }
  }

  /**
   * 거리 계산 (API) - 정확한 경로 기반
   */
  async calculateDistanceAPI(origin, destination = 'BEXCO') {
    const endpoint = `${this.baseURL}/calculate-distance`;
    try {
      const response = await apiClient.get(endpoint, {
        params: { origin, destination },
      });
      
      // 🔥🔥🔥 [수정] apiClient는 data를 직접 반환
      const responseData = extractResponseData(response);
      return responseData?.distance;
    } catch (error) {
      logger.error('거리 계산 API 실패, 로컬 데이터 사용:', error);
      return this.calculateDistanceLocal(origin);
    }
  }

  /**
   * 탄소 배출량 계산 (로컬) - 기존 호환 (kg 단위)
   */
  calculateEmission(transportType, distance, passengers = 1) {
    const factor = EMISSION_FACTORS[transportType] || EMISSION_FACTORS.car;
    const baselineFactor = EMISSION_FACTORS.car;

    const baselineEmission = distance * baselineFactor;
    let actualEmission = distance * factor;

    if (transportType === 'car' && passengers > 1) {
      actualEmission = actualEmission / passengers;
    }

    const savedEmission = Math.max(0, baselineEmission - actualEmission);
    const points = Math.round(savedEmission * 10);
    const trees = savedEmission / 22;

    return {
      baselineEmission: Number(baselineEmission.toFixed(2)),
      actualEmission: Number(actualEmission.toFixed(2)),
      savedEmission: Number(savedEmission.toFixed(2)),
      points,
      trees: Number(trees.toFixed(2)),
    };
  }

  /**
   * 환경 영향 계산
   */
  calculateEnvironmentalImpact(carbonSaved) {
    try {
      const saved = parseFloat(carbonSaved) || 0;

      return {
        trees: Math.round((saved / 21) * 10) / 10,
        carMiles: Math.round((saved / 0.404) * 10) / 10,
        electricity: Math.round((saved / 0.92) * 10) / 10,
        plasticBottles: Math.round(saved * 8.3),
      };
    } catch (error) {
      logger.error('❌ 환경 영향 계산 실패:', error);
      return {
        trees: 0,
        carMiles: 0,
        electricity: 0,
        plasticBottles: 0,
      };
    }
  }

  /**
   * 배출 계수 조회 (로컬)
   */
  getEmissionFactor(transportType) {
    return EMISSION_FACTORS[transportType] || EMISSION_FACTORS.car;
  }

  /**
   * 사용 가능한 교통수단 목록
   */
  getTransportTypes() {
    return [
      { value: 'car', label: '승용차', icon: '🚗', factor: EMISSION_FACTORS.car },
      { value: 'bus', label: '고속버스', icon: '🚌', factor: EMISSION_FACTORS.bus },
      { value: 'train', label: 'KTX/열차', icon: '🚄', factor: EMISSION_FACTORS.train },
      { value: 'plane', label: '항공', icon: '✈️', factor: EMISSION_FACTORS.plane },
      { value: 'bicycle', label: '자전거', icon: '🚲', factor: EMISSION_FACTORS.bicycle },
      { value: 'walk', label: '도보', icon: '🚶', factor: EMISSION_FACTORS.walk },
      {
        value: 'public_transit',
        label: '대중교통',
        icon: '🚇',
        factor: EMISSION_FACTORS.public_transit,
      },
      {
        value: 'electric_car',
        label: '전기차',
        icon: '🔋',
        factor: EMISSION_FACTORS.electric_car,
      },
    ];
  }

  /**
   * 도시 목록 조회
   */
  getCities() {
    return Object.entries(DISTANCE_TO_BEXCO)
      .filter(([city]) => city !== '기타')
      .map(([city, distance]) => ({
        value: city,
        label: city,
        distance,
      }));
  }

  /**
   * 🔥 [추가] 해외 국가 목록 조회
   * @returns {Array} 국가 목록 (코드, 이름, 좌표)
   */
  getOverseasCountries() {
    return Object.entries(OVERSEAS_COORDINATES)
      .filter(([code]) => !code.includes('_')) // 주요 도시 제외, 국가만
      .map(([code, data]) => ({
        code,
        name: data.names[0], // 한국어 이름
        nameEn: data.names.find((n) => /^[a-z]/.test(n)) || data.names[1], // 영어 이름
        lat: data.lat,
        lon: data.lon,
      }));
  }

  /**
   * 🔥 [추가] 특정 국가의 주요 도시 목록 조회
   * @param {string} countryCode - 국가 코드 (예: 'JP', 'CN', 'US')
   * @returns {Array} 도시 목록
   */
  getOverseasCities(countryCode) {
    const cities = [];
    const prefix = `${countryCode}_`;

    for (const [code, data] of Object.entries(OVERSEAS_COORDINATES)) {
      if (code.startsWith(prefix)) {
        cities.push({
          code,
          name: data.names[0],
          lat: data.lat,
          lon: data.lon,
        });
      }
    }

    return cities;
  }

  // =============================================================================
  // 🔥🔥🔥 [2025-12-18 수정] 로컬 스토리지 동기화 (설문 결과 기반 탄소 데이터)
  // =============================================================================

  /**
   * 🌱 [수정] 로컬 스토리지의 설문 결과를 기반으로 탄소 데이터 동기화
   */
  async syncCarbonDataFromLocal() {
    try {
      logger.debug('🌱 [CarbonService] 로컬 탄소 데이터 동기화 시작...');
      
      const { user_id, device_id, isGuest } = getCurrentIdentifier();
      
      // 1. localStorage에서 설문 결과 읽기
      const surveyDataRaw = localStorage.getItem('onboarding_survey_result');
      const carbonDataRaw = localStorage.getItem('carbon_tracking_data');
      
      if (!surveyDataRaw && !carbonDataRaw) {
        logger.debug('📋 [CarbonService] 동기화할 로컬 데이터 없음');
        return { success: true, synced: false, message: '동기화할 데이터 없음' };
      }
      
      let totalCarbonSaved = 0;
      let syncedItems = 0;
      
      // 2. 설문 결과에서 교통 정보 추출 및 계산
      if (surveyDataRaw) {
        try {
          const surveyData = JSON.parse(surveyDataRaw);
          logger.debug('📝 [CarbonService] 설문 데이터 발견:', surveyData);
          
          // 이미 동기화된 데이터인지 확인
          if (surveyData._synced) {
            logger.debug('📋 [CarbonService] 이미 동기화된 설문 데이터, 스킵');
          } else {
            // 설문에서 교통수단과 출발지 정보 추출
            const transportType = surveyData.transportation_type || surveyData.transport_mode;
            const region = surveyData.region || surveyData.departure_region;
            const district = surveyData.district || surveyData.departure_district;
            
            if (transportType && region) {
              // 거리 계산
              const distance = this.calculateDistanceByLocation(region, district);

              // ✈️ 도메스틱/인터내셔널 자동 판정:
              //   region 이 home country 코드 (KR / HK) 거나 시/도/광역구 명이면 domestic
              //   region 이 'OVERSEAS' 거나 외국 식별자면 international
              const homeCountryCodes = new Set(['KR', 'HK']);
              const overseasFlags = new Set(['OVERSEAS', '해외', 'overseas']);
              const isDomestic = !overseasFlags.has(region)
                && !(typeof region === 'string' && region.length === 2 && !homeCountryCodes.has(region));

              const carbonResult = this.calculateCarbonReduction(distance, transportType, { isDomestic });
              
              logger.debug('🌍 [CarbonService] 탄소 계산 결과:', {
                distance,
                transportType,
                reductionKg: carbonResult.reductionKg,
                isNegative: carbonResult.isNegative,
                isGuest
              });
              
              // 감축량 합산 (음수도 그대로 - 초과 배출 표시용)
              totalCarbonSaved += carbonResult.reductionKg;
              syncedItems++;
              
              // 3. 서버에 동기화 시도 (실패해도 로컬 업데이트는 진행)
              try {
                await this.trackCarbonActivity({
                  activity_type: 'transportation',
                  transportation_type: transportType,
                  distance: distance,
                  saved_co2: carbonResult.reductionKg,
                  source: 'onboarding_survey',
                  device_id: isGuest ? device_id : null
                });
                logger.debug('✅ [CarbonService] 서버 동기화 성공');
                
                // 동기화 성공 시 로컬 데이터에 플래그 추가
                surveyData._synced = true;
                surveyData._syncedAt = new Date().toISOString();
                localStorage.setItem('onboarding_survey_result', JSON.stringify(surveyData));
                
              } catch (serverError) {
                logger.warn('⚠️ [CarbonService] 서버 동기화 실패 (로컬은 유지):', serverError.message);
              }
            }
          }
        } catch (parseError) {
          logger.error('❌ [CarbonService] 설문 데이터 파싱 실패:', parseError);
        }
      }
      
      // 4. 추가 탄소 추적 데이터 처리 (있는 경우)
      if (carbonDataRaw) {
        try {
          const carbonData = JSON.parse(carbonDataRaw);
          
          if (Array.isArray(carbonData)) {
            for (const item of carbonData) {
              if (!item._synced && item.carbon_saved) {
                totalCarbonSaved += parseFloat(item.carbon_saved) || 0;
                syncedItems++;
              }
            }
          } else if (carbonData.carbon_saved && !carbonData._synced) {
            totalCarbonSaved += parseFloat(carbonData.carbon_saved) || 0;
            syncedItems++;
          }
        } catch (parseError) {
          logger.error('❌ [CarbonService] 탄소 데이터 파싱 실패:', parseError);
        }
      }
      
      // 5. authStore 업데이트 (UI 즉시 반영) - 로그인 사용자만
      if (syncedItems > 0 && !isGuest) {
        const currentUser = this._getCurrentUser();
        
        if (currentUser) {
          const existingSaved = currentUser.total_carbon_saved || 0;
          
          const needsUpdate = existingSaved === 0 || 
            !localStorage.getItem('carbon_sync_completed');
          
          if (needsUpdate) {
            this._updateUserData(0, totalCarbonSaved);
            localStorage.setItem('carbon_sync_completed', 'true');
            logger.debug('✅ [CarbonService] authStore 업데이트 완료:', {
              이전값: existingSaved,
              추가값: totalCarbonSaved,
              합계: existingSaved + totalCarbonSaved
            });
          } else {
            logger.debug('📋 [CarbonService] 이미 동기화 완료됨, 스킵');
          }
        }
      }
      
      logger.debug('🌱 [CarbonService] 로컬 동기화 완료:', {
        syncedItems,
        totalCarbonSaved: totalCarbonSaved.toFixed(2) + 'kg',
        isGuest
      });
      
      return {
        success: true,
        synced: syncedItems > 0,
        syncedItems,
        totalCarbonSaved,
        isGuest
      };
      
    } catch (error) {
      this._logError('로컬 탄소 데이터 동기화', error);
      logger.error('❌ [CarbonService] 동기화 실패:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🌱 [신규] 동기화 상태 초기화 (재동기화 필요 시)
   */
  resetSyncStatus() {
    localStorage.removeItem('carbon_sync_completed');
    
    // 설문 데이터의 _synced 플래그도 제거
    const surveyDataRaw = localStorage.getItem('onboarding_survey_result');
    if (surveyDataRaw) {
      try {
        const surveyData = JSON.parse(surveyDataRaw);
        delete surveyData._synced;
        delete surveyData._syncedAt;
        localStorage.setItem('onboarding_survey_result', JSON.stringify(surveyData));
      } catch (e) {
        // 무시
      }
    }
    
    logger.debug('🔄 [CarbonService] 동기화 상태 초기화 완료');
  }

  /**
   * 🔥🔥🔥 [신규] 로컬에 탄소 데이터 임시 저장 (오프라인 지원)
   */
  saveToLocal(activityData) {
    try {
      const STORAGE_KEY = 'carbon_local_data';
      const localData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      
      localData.push({
        ...activityData,
        saved_at: new Date().toISOString()
      });
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(localData));
      
      logger.debug('[carbonService] 로컬에 탄소 데이터 저장 완료');
      
      return true;
    } catch (error) {
      logger.error('[carbonService] 로컬 저장 실패:', error);
      return false;
    }
  }

  /**
   * 로컬 설문 데이터에서 탄소 절감 계산 (프론트엔드용)
   * @param {Object} surveyData - 설문 응답 데이터
   * @returns {Object} 탄소 절감 계산 결과
   */
  calculateFromSurveyData(surveyData) {
    try {
      const { location, transportMode } = surveyData;
      
      if (!location || !transportMode) {
        logger.warn('[carbonService] 설문 데이터에 위치 또는 교통수단 정보 없음');
        return null;
      }
      
      // 거리 계산
      const distanceKm = this.calculateDistanceByLocation(location.region, location.district);
      
      // 프론트엔드 transportMode를 백엔드 형식으로 변환
      const TRANSPORT_MAPPING = {
        'walk': 'walking',
        'walk_bicycle': 'walking',
        'bicycle': 'bicycle',
        'public': 'public_bus',
        'public_bus': 'public_bus',
        'subway': 'subway',
        'train': 'train',
        'car': 'car_gasoline',
        'car_internal': 'car_gasoline',
        'car_gasoline': 'car_gasoline',
        'car_electric': 'car_electric',
        'airplane': 'airplane',
        'taxi': 'taxi'
      };
      
      const normalizedTransportMode = TRANSPORT_MAPPING[transportMode.toLowerCase()] || 'car_gasoline';
      
      // 탄소 절감량 계산 (기존 calculateCarbonReduction 메서드 재사용)
      const result = this.calculateCarbonReduction(distanceKm, normalizedTransportMode);
      
      logger.debug('[carbonService] 설문 데이터 탄소 계산 결과:', result);
      
      return {
        carbon_saved_kg: result.reductionKg,
        points_earned: Math.round(result.reductionKg * 10),
        emission_factor: result.factor,
        distance_km: result.distanceKm,
        transport_mode: normalizedTransportMode
      };
    } catch (error) {
      logger.error('[carbonService] 설문 데이터 탄소 계산 실패:', error);
      return null;
    }
  }

  /**
   * 상수 내보내기 (외부에서 접근 필요 시)
   */
  getConstants() {
    return {
      DISTANCE_TO_BEXCO,
      EMISSION_FACTORS,
      EMISSION_FACTORS_GRAMS,
      BASELINE_EMISSION_GRAMS,
      BEXCO_COORDS,
      DISTRICT_COORDINATES,
      COUNTRY_DISTANCES,
      OVERSEAS_COORDINATES,
    };
  }
}

const carbonService = new CarbonService();

// 개발 환경에서 정보 출력
if (import.meta.env?.DEV) {
  setTimeout(() => {
    logger.debug('✅ CarbonService loaded (BEXCO 통합 버전 + 2단계 선택 UI + 해외 좌표 지원 + 게스트(device_id) 지원 + apiClient 호환성 수정 v4.9)');
  }, 1000);
}

export default carbonService;

// 상수도 내보내기 (필요 시 직접 사용)
export {
  DISTANCE_TO_BEXCO,
  EMISSION_FACTORS,
  EMISSION_FACTORS_GRAMS,
  BASELINE_EMISSION_GRAMS,
  BEXCO_COORDS,
  DISTRICT_COORDINATES,
  COUNTRY_DISTANCES,
  OVERSEAS_COORDINATES,
  getCurrentIdentifier,
  getDeviceId,
};